import _ from 'lodash';

import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AppConfigService } from './app-config.service';
import { MembersService } from './members.service';
import { PersonDetailsService } from 'src/app/shared/services/person-details.service';
import { TeamService } from './team.service';
import { Person } from 'src/app/people/models/person';
import { Team } from 'src/app/shared/models/team/team';
import { PersonForMemberRequests } from 'src/app/people/models/personForApplication';
import { PersonProfileService } from 'src/app/people/profile/person-profile.service';
import { Application } from '../models/application/application';
import { SurveyForm } from '../models/survey/surveyForm';
import { SurveyService } from './survey.service';
import { ApplicationService } from './application.service';
import { SeatInvitation } from 'src/shared-app-portal/components/lineup-applications/invitation/seatInvitation';
import { EvaluationForm } from 'src/shared-app-portal/components/lineup-applications/evaluation/models/evaluationForm';
import { MemberEvaluationService } from 'src/app/members/member-evaluation.service';
import { InjectorModule } from '../modules/injector.module';
import { AccessibilityService } from './accessibility.service';
const defaultPageTitle = 'lineup';

@Injectable({
  providedIn: 'root'
})
export class DynamicTitleService {
  public titleUpdated = new Subject<string>();
  private routeTitle: string = null;
  private selectedPerson: Person | PersonForMemberRequests = null;
  private selectedSeatInvitation: SeatInvitation = null;
  private selectedTeam: Team = null;
  private selectedEvaluation: EvaluationForm = null;
  private selectedSurvey: SurveyForm = null;
  private selectedApplication: Application = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private appConfigService: AppConfigService,
    private membersService: MembersService,
    private evalService: MemberEvaluationService,
    private surveyService: SurveyService,
    private applicationService: ApplicationService,
    private accessibilityService: AccessibilityService
  ) {
    this.initForAnonymous();
    // Subscribing to the routing hooks - navigation end only in this case
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      const rt = this.getChild(this.route);
      rt.data.subscribe((data) => {
        if (data.clearDynamicTitleState) {
          this.selectedPerson = null;
          this.selectedSeatInvitation = null;
          this.selectedTeam = null;
          this.selectedEvaluation = null;
          this.selectedSurvey = null;
          this.selectedApplication = null;
        }

        this.routeTitle =
          data.title ||
          (this.appConfigService.tenant !== null ? this.appConfigService.tenant.name + ' ' : '') +
            defaultPageTitle;
        this.updateTitle();
      });
    });
  }

  public initForAuthenticated() {
    const personDetailsService: PersonDetailsService =
      InjectorModule.injector.get(PersonDetailsService);
    const teamService: TeamService = InjectorModule.injector.get(TeamService);
    const profileService: PersonProfileService = InjectorModule.injector.get(PersonProfileService);
    personDetailsService.personFetched
      .pipe(filter((person) => person !== null))
      .subscribe((person: Person) => {
        this.selectedPerson = person;
        this.updateTitle();
      });

    profileService.person.pipe(filter((person) => person !== null)).subscribe((person: Person) => {
      this.selectedPerson = person;
      this.updateTitle();
    });

    teamService.team.pipe(filter((team) => team !== null)).subscribe((team: Team) => {
      this.selectedTeam = team;
      this.updateTitle();
    });
  }

  public initForAnonymous() {
    // Next, we need to subscribe to members, person, and team services
    // These fire events after fetching entities - we just need to make sure objects are non-null
    this.membersService.seatInvitationFetched
      .pipe(filter((seatInvitation) => seatInvitation !== null))
      .subscribe((seatInvitation: SeatInvitation) => {
        this.selectedSeatInvitation = seatInvitation;
        this.updateTitle();
      });

    this.membersService.personFetched
      .pipe(filter((person) => person !== null))
      .subscribe((person: PersonForMemberRequests) => {
        this.selectedPerson = person;
        this.updateTitle();
      });
    this.evalService.evaluation
      .pipe(filter((team) => team !== null))
      .subscribe((evaluation: EvaluationForm) => {
        this.selectedEvaluation = evaluation;
        this.updateTitle();
      });
    this.surveyService.survey
      .pipe(filter((team) => team !== null))
      .subscribe((survey: SurveyForm) => {
        this.selectedSurvey = survey;
        this.updateTitle();
      });
    this.applicationService.application
      .pipe(filter((team) => team !== null))
      .subscribe((application: Application) => {
        this.selectedApplication = application;
        this.updateTitle();
      });
  }

  /**
   * Gets the last child of the route user is navigating into
   * @param activatedRoute { ActivatedRoute } route user is navigating into
   * @returns {ActivatedRoute} last child of the route sent as parameter
   */
  private getChild(activatedRoute: ActivatedRoute) {
    if (activatedRoute.firstChild) {
      return this.getChild(activatedRoute.firstChild);
    } else {
      return activatedRoute;
    }
  }

  /**
   * Emits the title that will be used by app.component so that is
   * applied to the browser using platform-browser title services
   */
  private updateTitle() {
    let returnTitle = this.routeTitle;

    if (returnTitle !== null) {
      returnTitle = this.appConfigService.getPlatformLanguage(returnTitle);
      if (returnTitle.indexOf('${person}') !== -1 && this.selectedPerson !== null) {
        returnTitle = returnTitle.replace('${person}', this.selectedPerson.fullName);
      }

      if (returnTitle.indexOf('${team}') !== -1 && this.selectedSeatInvitation !== null) {
        returnTitle = returnTitle.replace('${team}', this.selectedSeatInvitation.teamName);
      }

      if (returnTitle.indexOf('${team}') !== -1 && this.selectedTeam !== null) {
        returnTitle = returnTitle.replace('${team}', this.selectedTeam.name);
      }

      if (returnTitle.indexOf('${application}') !== -1 && this.selectedApplication !== null) {
        returnTitle = returnTitle.replace('${application}', this.selectedApplication.form.title);
      }

      if (returnTitle.indexOf('${evaluation}') !== -1 && this.selectedEvaluation !== null) {
        returnTitle = returnTitle.replace('${evaluation}', this.selectedEvaluation.title);
      }

      if (returnTitle.indexOf('${survey}') !== -1 && this.selectedSurvey !== null) {
        returnTitle = returnTitle.replace('${survey}', this.selectedSurvey.title);
      }

      if (returnTitle.indexOf('${tenant}') !== -1 && this.appConfigService.tenant !== null) {
        returnTitle = returnTitle.replace('${tenant}', this.appConfigService.tenant.name);
      }

      const tokens = returnTitle.match(/\${\w+}/g);

      if (tokens == null || tokens.length === 0) {
        this.titleUpdated.next(returnTitle);
        this.accessibilityService.talk(returnTitle);
      } else {
        const fallbackTitle =
          (this.appConfigService.tenant !== null ? this.appConfigService.tenant.name + ' ' : '') +
          defaultPageTitle;
        this.titleUpdated.next(fallbackTitle);
        this.accessibilityService.talk(fallbackTitle);
      }
    }
  }
}
